import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import {
  Badge,
  Button,
  CheckboxField,
  Field,
  InlineAlert,
  Select,
} from '@aeris/ui'

import {
  ApiError,
  assignFiscalTaxProfile,
  createNfe,
  fetchFiscalSaleCheck,
  fetchFiscalTaxProfiles,
  fiscalDocumentFileUrl,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'

/**
 * A nota fiscal na ficha da venda (F373). Só aparece com a chave `fiscal`: as
 * outras contas continuam vendo a ficha como sempre foi.
 */

const SITUATION: Readonly<Record<string, string>> = {
  pendente: 'Na fila',
  processando: 'Enviando',
  autorizada: 'Autorizada',
  rejeitada: 'Recusada',
  incerta: 'Sem resposta, conferindo',
  cancelando: 'Cancelando',
  cancelada: 'Cancelada',
}

export const SaleFiscalPanel = ({ saleId }: { saleId: string }): ReactNode => {
  const { allows, hasFeature } = useAuth()
  const visible = hasFeature('fiscal') && allows('fiscal.read')
  return visible ? <Panel saleId={saleId} /> : null
}

const Panel = ({ saleId }: { saleId: string }): ReactNode => {
  const { allows } = useAuth()
  const queryClient = useQueryClient()
  // Nasce uma vez por ficha aberta: dois cliques não viram duas notas.
  const [clientRequestId, setClientRequestId] = useState(() =>
    crypto.randomUUID()
  )
  const [finalConsumer, setFinalConsumer] = useState<boolean | null>(null)

  const queryKey = ['fiscal-sale-check', saleId]
  const check = useQuery({
    queryKey,
    queryFn: ({ signal }) => fetchFiscalSaleCheck(saleId, signal),
    refetchInterval: (query) =>
      ['pendente', 'processando', 'incerta', 'cancelando'].includes(
        query.state.data?.document?.status ?? ''
      )
        ? 5_000
        : false,
  })
  const profiles = useQuery({
    queryKey: ['fiscal-tax-profiles'],
    queryFn: ({ signal }) => fetchFiscalTaxProfiles(signal),
    enabled:
      check.data?.problems.some(
        (problem) => problem.code === 'line_no_profile'
      ) === true,
  })

  const assign = useMutation({
    meta: { errorShownInline: true },
    mutationFn: ({
      variantId,
      profileId,
    }: {
      variantId: string
      profileId: string
    }) => assignFiscalTaxProfile(variantId, { profileId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  })

  const emit = useMutation({
    meta: { errorShownInline: true },
    mutationFn: () =>
      createNfe({
        clientRequestId,
        saleId,
        finalConsumer:
          finalConsumer ?? check.data?.finalConsumerSuggested ?? true,
        presence: '1',
        operationNature: 'VENDA DE MERCADORIA',
      }),
    onSuccess: async () => {
      setClientRequestId(crypto.randomUUID())
      await queryClient.invalidateQueries({ queryKey })
    },
  })

  if (check.isPending) return null
  if (check.isError) {
    return (
      <InlineAlert tone="danger">
        {check.error instanceof ApiError
          ? check.error.message
          : 'Não foi possível ler a nota fiscal desta venda.'}
      </InlineAlert>
    )
  }

  const { document, problems, finalConsumerSuggested } = check.data
  const living =
    document !== null &&
    document.status !== 'rejeitada' &&
    document.status !== 'cancelada'
  const consumer = finalConsumer ?? finalConsumerSuggested
  const failure = emit.isError
    ? emit.error instanceof ApiError
      ? emit.error.message
      : 'Não foi possível emitir agora.'
    : null

  return (
    <div className="flex flex-col gap-2 rounded-(--radius-md) border border-(--border-subtle) p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">Nota fiscal</span>
        {document === null ? (
          <Badge tone="neutral">Não emitida</Badge>
        ) : (
          <>
            <Badge
              tone={document.status === 'autorizada' ? 'success' : 'neutral'}
            >
              {SITUATION[document.status] ?? document.status}
            </Badge>
            <span className="text-(length:--text-sm) text-(--text-secondary)">
              Nº {document.number}
              {document.environment === 'homologacao' ? ' (teste)' : ''}
            </span>
            {document.hasPdf ? (
              <a
                className="text-(length:--text-sm) underline"
                href={fiscalDocumentFileUrl(document.id, 'pdf')}
                target="_blank"
                rel="noreferrer"
              >
                PDF
              </a>
            ) : null}
          </>
        )}
      </div>

      {document?.rejectionMessage == null ? null : (
        <p className="text-(length:--text-xs) text-(--text-secondary)">
          {document.rejectionMessage}
        </p>
      )}

      {living || !allows('fiscal.emit') ? null : (
        <>
          {problems.length === 0 ? null : (
            <ul className="list-disc pl-5 text-(length:--text-sm) text-(--text-secondary)">
              {problems.map((problem) => (
                <li key={`${problem.code}-${problem.variantId ?? ''}`}>
                  {problem.message}
                  {problem.code === 'line_no_profile' &&
                  problem.variantId !== undefined ? (
                    <ProfilePicker
                      options={(profiles.data?.profiles ?? []).filter(
                        (profile) => !profile.archived
                      )}
                      loading={assign.isPending}
                      onPick={(profileId) => {
                        assign.mutate({
                          variantId: problem.variantId ?? '',
                          profileId,
                        })
                      }}
                    />
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          <CheckboxField
            label="O cliente é o consumidor final (não vai revender)"
            checked={consumer}
            onCheckedChange={(checked) => {
              setFinalConsumer(checked === true)
            }}
          />
          {failure === null ? null : (
            <InlineAlert tone="danger">{failure}</InlineAlert>
          )}
          {document?.status === 'rejeitada' ? (
            <p className="text-(length:--text-xs) text-(--text-secondary)">
              Corrigiu o motivo? Em Notas fiscais, Enviar de novo mantém o mesmo
              número. Se a venda mudou de valor, emita outra nota aqui.
            </p>
          ) : null}
          <div>
            <Button
              variant="primary"
              loading={emit.isPending}
              disabled={problems.length > 0}
              onClick={() => {
                emit.mutate()
              }}
            >
              {document?.status === 'rejeitada'
                ? 'Emitir outra nota fiscal'
                : 'Emitir nota fiscal'}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

const ProfilePicker = ({
  options,
  loading,
  onPick,
}: {
  options: readonly { id: string; name: string }[]
  loading: boolean
  onPick: (profileId: string) => void
}): ReactNode =>
  options.length === 0 ? (
    <span className="block text-(length:--text-xs)">
      Cadastre um perfil em Configurações, na aba Nota fiscal.
    </span>
  ) : (
    <Field label="Perfil de imposto" className="mt-1 max-w-72">
      {(field) => (
        <Select
          {...field}
          disabled={loading}
          value=""
          onChange={(event) => {
            if (event.target.value !== '') onPick(event.target.value)
          }}
        >
          <option value="">Escolha</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </Select>
      )}
    </Field>
  )
